import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function DashboardPage() {
  const { profile, signOut, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold font-heading text-primary-600">CotIdel.</span>
              <span className="text-xs text-foreground-400 hidden sm:inline">Tableau de bord</span>
            </div>
            <div className="flex items-center gap-3">
              {profile?.role === 'admin' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-semibold uppercase tracking-wide">
                  <i className="ri-shield-star-line"></i>
                  Administrateur
                </span>
              )}
              <span className="text-xs text-foreground-500 hidden sm:inline">
                {profile?.first_name} {profile?.last_name}
              </span>
              {profile?.role === 'admin' && (
                <Link
                  to="/admin"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-semibold hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-shield-star-line"></i>
                  Administration
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-logout-box-r-line"></i>
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-6 flex items-center gap-3">
          Bonjour, {profile?.first_name || 'IDEL'}
          {profile?.role === 'admin' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
              <i className="ri-shield-star-line"></i>
              Compte administrateur
            </span>
          )}
        </h1>

        {/* Stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-stethoscope-line text-primary-600 text-lg"></i>
              </div>
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Analyses ce mois</span>
            </div>
            <p className="text-3xl font-bold text-foreground-950">{profile?.analyses_this_month ?? 0}</p>
            <p className="text-xs text-foreground-400 mt-1">Compteur mensuel</p>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-vip-crown-line text-accent-600 text-lg"></i>
              </div>
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Abonnement</span>
            </div>
            <p className="text-sm font-bold text-foreground-950">
              {profile?.subscription_status === 'active' ? 'Actif' : profile?.subscription_status ?? 'Inconnu'}
            </p>
            {profile?.subscription_end && (
              <p className="text-xs text-foreground-400 mt-1">
                Jusqu&apos;au {new Date(profile.subscription_end).toLocaleDateString('fr-FR')}
              </p>
            )}
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-file-list-3-line text-secondary-600 text-lg"></i>
              </div>
              <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Identité</span>
            </div>
            <p className="text-sm font-bold text-foreground-950">{profile?.adeli_number || '—'}</p>
            <p className="text-xs text-foreground-400 mt-1">N° ADELI</p>
          </div>
        </div>

        {/* Quick actions */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 mb-8">
          <h2 className="text-base font-bold font-heading text-foreground-950 mb-4">Actions rapides</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/analysis')}
              className="flex-1 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-5 py-3 rounded-md text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-brain-line"></i>
              Nouvelle analyse de cotation
            </button>
            <button
              onClick={() => navigate('/profile')}
              className="flex-1 bg-background-100 hover:bg-background-200 text-foreground-800 font-semibold px-5 py-3 rounded-md text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 border border-background-300/60"
            >
              <i className="ri-user-settings-line"></i>
              Mon profil
            </button>
          </div>
        </div>

        {/* Account info */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
          <h2 className="text-base font-bold font-heading text-foreground-950 mb-4">Informations du compte</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-foreground-400 block mb-0.5">Email</span>
              <span className="text-sm font-medium text-foreground-800">{user?.email || '—'}</span>
            </div>
            <div>
              <span className="text-xs text-foreground-400 block mb-0.5">Nom complet</span>
              <span className="text-sm font-medium text-foreground-800">{profile?.first_name || '—'} {profile?.last_name || '—'}</span>
            </div>
            <div>
              <span className="text-xs text-foreground-400 block mb-0.5">N° ADELI</span>
              <span className="text-sm font-medium text-foreground-800">{profile?.adeli_number || '—'}</span>
            </div>
            <div>
              <span className="text-xs text-foreground-400 block mb-0.5">N° SIRET</span>
              <span className="text-sm font-medium text-foreground-800">{profile?.siret || '—'}</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}